import React, { useEffect, useState } from 'react';
import Icon from '../Icon';
import SubtitleEditForm from './SubtitleEditForm';
import { deleteSubtitle } from './subtitleStore';
import { formatTime, showVideoAt } from './momentTime';
import { hasEnd } from '../../utils/timeline';
import './SubtitlePanel.css';

const formatTimeDisplay = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * SubtitleRow — one subtitle: tap to seek (and select it for word
 * annotation), edit in place, delete with its annotations after a
 * confirmation. Used by the "Now" panel and the Subtitles list.
 *
 * Props:
 *   subtitle, active, currentTime, rowRef
 *   onSelect(subtitle), onEdited(updated, movedAnnotations, neighbours), onDeleted(id)
 *   onEditingChange(editing) — the row is open for editing or deleting
 *   actions — optional: more buttons, before edit and delete
 */
function SubtitleRow({ subtitle, active, currentTime, rowRef, onSelect, onEdited, onDeleted, onEditingChange, actions }) {
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'delete'
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    onEditingChange && onEditingChange(mode !== 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteSubtitle(subtitle.id);
      setMode('view');
      onDeleted && onDeleted(subtitle.id);
    } catch (e) {
      console.error('Error deleting subtitle:', e);
      setDeleting(false);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="subtitle-list-item subtitle-list-item--editing" ref={rowRef}>
        <SubtitleEditForm
          subtitle={subtitle}
          currentTime={currentTime}
          onSaved={(updated, moved, neighbours) => { setMode('view'); onEdited && onEdited(updated, moved, neighbours); }}
          onCancel={() => setMode('view')}
        />
      </div>
    );
  }

  return (
    <div ref={rowRef} className={`subtitle-list-item ${active ? 'subtitle-list-item--active' : ''}`}>
      {mode === 'delete' ? (
        <div className="subtitle-list-delete-confirm">
          <p>Delete this subtitle and all its annotations?</p>
          <div className="subtitle-list-delete-actions">
            <button className="subtitle-list-delete-yes" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
            <button className="subtitle-list-delete-no" onClick={() => setMode('view')}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <button
            className="subtitle-list-content"
            onClick={() => {
              onSelect && onSelect(subtitle);
              showVideoAt(subtitle.startTime); // paused there
            }}
            aria-label={`Subtitle at ${formatTimeDisplay(subtitle.startTime)}: ${subtitle.originalText || subtitle.englishText}`}
          >
            {/* The one time for the subtitle and the words aligned with it */}
            <span className="subtitle-list-time subtitle-list-range">
              {formatTime(subtitle.startTime)} → {hasEnd(subtitle) ? formatTime(subtitle.endTime) : 'next'}
            </span>
            <span className="subtitle-list-text">
              {subtitle.originalText && <span className="subtitle-list-original">{subtitle.originalText}</span>}
              {subtitle.englishText && <span className="subtitle-list-english">{subtitle.englishText}</span>}
            </span>
          </button>
          <div className="subtitle-list-actions">
            {actions}
            <button className="icon-btn icon-btn--sm" onClick={() => setMode('edit')} aria-label="Edit subtitle">
              <Icon name="pencil" size={15} />
            </button>
            <button className="icon-btn icon-btn--sm icon-btn--danger" onClick={() => setMode('delete')} aria-label="Delete subtitle">
              <Icon name="x" size={15} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default SubtitleRow;
